import { useState } from 'react';

import { router, usePage } from '@inertiajs/react';
import { isEmpty } from 'lodash';

import { SellerCatalogNavigation } from './SellerCatalogNavigation';
import { SellerCatalogSearch } from './SellerCatalogSearch';
import { SellerOffersGrid } from './SellerOffersGrid';
import { SellerProductTypeNavigation } from './SellerProductTypeNavigation';
import { SellerWishlistFilterButton } from './SellerWishlistFilterButton';
import { useResolvedTheme } from '../../hooks/useResolvedTheme';
import { FiltersService } from '../../services/FiltersService';
import {
    CurrentUser,
    Expansion,
    Offer,
    OfferProperty,
    ProductType,
    PublicUser,
    Universe,
} from '../../types';
import { DesktopFilters } from '../Products/filters/DesktopFilters';
import { DesktopFiltersLayout } from '../Products/filters/DesktopFiltersLayout';
import { MobileFilters } from '../Products/filters/MobileFilters';

type PageProps = {
    currentUser: CurrentUser | null;
    user: PublicUser;
    offers: Offer[] | null;
    offer_properties: OfferProperty[];
    catalogCount: number;
    currentUniverse: Universe | null;
    userUniverses: Universe[] | null;
    currentProductType: ProductType | null;
    currentExpansion: Expansion | null;
    userExpansions: Expansion[] | null;
    userProductTypes?: ProductType[] | null;
    nextPage: number | null;
    q: Record<string, string>;
    sort: string;
    searchQuery: string;
    wishlistOnly: boolean;
};

const WISHLIST_FILTER_KEY = 'wishlist';
const WISHLIST_FILTER_VALUE = 'only';

const withWishlistFilter = (url: string, enabled: boolean) => {
    if (!enabled) return url;

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${WISHLIST_FILTER_KEY}=${WISHLIST_FILTER_VALUE}`;
};

const Show = () => {
    const resolvedTheme = useResolvedTheme();
    const {
        currentUser,
        user,
        offers,
        offer_properties: offerProperties,
        catalogCount,
        currentUniverse,
        userUniverses,
        currentProductType,
        currentExpansion,
        userExpansions,
        userProductTypes,
        nextPage,
        q,
        sort,
        searchQuery,
        wishlistOnly,
    } = usePage<PageProps>().props;

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const safeUserUniverses = userUniverses ?? [];
    const safeUserProductTypes = userProductTypes ?? [];
    const defaultProductTypeSlug =
        safeUserProductTypes.length > 0
            ? safeUserProductTypes[0].slug
            : 'singles';
    const showUniverseFilter = safeUserUniverses.length > 1;
    const showSellerCatalogNavigation = showUniverseFilter;
    const showProductTypeNavigation =
        Boolean(currentUniverse) && safeUserProductTypes.length > 1;

    const applySearch = ({
        q,
        expansionSlug,
        preserveState,
    }: {
        q: Record<string, string>;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => {
        const catalogUrl = FiltersService.generateFilterURL({
            basePath: currentUniverse ? `/user/${user.nickname}` : '',
            filters: q,
            expansion: expansionSlug,
            productType: currentProductType?.slug ?? defaultProductTypeSlug,
            universe: currentUniverse?.url_path ?? '',
            sortBy: sort,
            search: searchQuery,
        });
        const url = withWishlistFilter(catalogUrl, wishlistOnly);

        router.get(
            url,
            {},
            {
                preserveState: Boolean(preserveState),
                onStart: () => setLoading(true),
                onSuccess: () => setLoading(false),
            },
        );
    };

    const handleSortChange = (value: string) => {
        const url = withWishlistFilter(
            FiltersService.generateFilterURL({
                basePath: currentUniverse ? `/user/${user.nickname}` : '',
                filters: q,
                expansion: currentExpansion?.slug,
                productType: currentProductType?.slug,
                universe: currentUniverse?.url_path ?? '',
                sortBy: value,
                search: searchQuery,
            }),
            wishlistOnly,
        );

        router.get(
            url,
            {},
            {
                preserveScroll: true,
                onStart: () => setLoading(true),
                onSuccess: () => setLoading(false),
            },
        );
    };

    const resetFilters = () => {
        const url = currentUniverse
            ? FiltersService.generateResetFilterUrl({
                  basePath: `/user/${user.nickname}`,
                  universe: currentUniverse.url_path,
                  productType: currentProductType?.slug,
                  search: searchQuery,
              })
            : searchQuery
              ? `/user/${user.nickname}?${new URLSearchParams({ search: searchQuery })}`
              : `/user/${user.nickname}`;

        router.get(url);
    };

    const toggleWishlistFilter = () => {
        const queryParams = new URLSearchParams(window.location.search);

        queryParams.delete('page');
        if (wishlistOnly) {
            queryParams.delete(WISHLIST_FILTER_KEY);
        } else {
            queryParams.set(WISHLIST_FILTER_KEY, WISHLIST_FILTER_VALUE);
        }

        const queryString = queryParams.toString();
        const url = queryString
            ? `${window.location.pathname}?${queryString}`
            : window.location.pathname;

        router.get(
            url,
            {},
            {
                preserveScroll: true,
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };

    const handleCatalogSearch = (nextQuery: string) => {
        const url = withWishlistFilter(
            currentUniverse
                ? FiltersService.generateFilterURL({
                      basePath: `/user/${user.nickname}`,
                      filters: q,
                      expansion: currentExpansion?.slug,
                      productType: currentProductType?.slug,
                      universe: currentUniverse.url_path,
                      sortBy: sort,
                      search: nextQuery,
                  })
                : sellerRootUrl(nextQuery),
            wishlistOnly,
        );

        router.get(
            url,
            {},
            {
                preserveScroll: true,
                preserveState: true,
                onStart: () => setLoading(true),
                onFinish: () => setLoading(false),
            },
        );
    };

    const sellerRootUrl = (nextQuery: string) => {
        const queryParams = new URLSearchParams();

        if (sort) queryParams.set('sort', sort);
        if (nextQuery.trim()) queryParams.set('search', nextQuery.trim());

        const queryString = queryParams.toString();
        const path = `/user/${user.nickname}`;

        return queryString ? `${path}?${queryString}` : path;
    };

    const sellerNavigationUrl = (path: string) => {
        const queryParams = new URLSearchParams();

        if (sort) queryParams.set('sort', sort);
        if (searchQuery) queryParams.set('search', searchQuery);

        const queryString = queryParams.toString();
        const url = queryString ? `${path}?${queryString}` : path;

        return withWishlistFilter(url, wishlistOnly);
    };

    const loadMore = () => {
        if (!nextPage || loading || loadingMore) return;

        router.get(
            window.location.href,
            { page: nextPage },
            {
                preserveScroll: true,
                preserveState: true,
                preserveUrl: true,
                only: ['offers', 'nextPage'],
                onStart: () => setLoadingMore(true),
                onFinish: () => setLoadingMore(false),
            },
        );
    };

    const areOffersEmpty =
        !(offers?.length ?? 0) &&
        isEmpty(q) &&
        isEmpty(currentExpansion) &&
        !searchQuery &&
        !wishlistOnly;
    const showSellerFilters = !areOffersEmpty && Boolean(currentUniverse);
    const productTypeNavigationItems = currentUniverse
        ? [
              {
                  href: sellerNavigationUrl(
                      `/user/${user.nickname}${currentUniverse.url_path}`,
                  ),
                  label: 'Усі товари',
                  active: !currentProductType,
                  testId: 'seller-product-types-all',
              },
              ...safeUserProductTypes.map((productType) => ({
                  href: sellerNavigationUrl(
                      `/user/${user.nickname}${currentUniverse.url_path}/${productType.slug}`,
                  ),
                  label: productType.name,
                  active: currentProductType?.slug === productType.slug,
                  testId: `seller-product-types-${productType.slug}`,
              })),
          ]
        : [];
    const desktopSellerProductTypeNavigation = showProductTypeNavigation ? (
        <SellerProductTypeNavigation
            items={productTypeNavigationItems}
            dataTestId="seller-product-types-section-nav"
        />
    ) : undefined;
    const mobileSellerProductTypeNavigation = showProductTypeNavigation ? (
        <SellerProductTypeNavigation
            items={productTypeNavigationItems}
            singleRow
        />
    ) : undefined;
    const universeNavigationItems = showUniverseFilter
        ? safeUserUniverses.map((universe) => ({
              href: sellerNavigationUrl(
                  `/user/${user.nickname}${universe.url_path}`,
              ),
              image:
                  resolvedTheme === 'dark'
                      ? universe.slider_image_dark
                      : universe.slider_image,
              name: universe.name,
              slug: universe.slug,
              active: currentUniverse?.slug === universe.slug,
              testId: `seller-universe-card-${universe.slug}`,
          }))
        : [];

    const catalogHeading = currentProductType?.name
        ? currentProductType.name
        : currentUniverse
          ? `Усі товари у ${currentUniverse.name}`
          : 'Усі товари продавця';

    const catalogSubtitle = wishlistOnly
        ? searchQuery
            ? `У списку бажаного за запитом «${searchQuery}»`
            : 'Товари зі списку бажаного, які є в цього продавця.'
        : searchQuery
          ? `За запитом «${searchQuery}»`
          : currentUniverse
            ? 'Фільтруйте й сортуйте каталог у вибраному всесвіті.'
            : 'Переглядайте весь каталог продавця на одній сторінці.';

    const catalogSectionClass = currentUniverse
        ? !areOffersEmpty
            ? 'pt-1 pb-8'
            : 'py-8'
        : 'pt-1 pb-8 sm:pt-2';

    const catalogContainerClass = currentUniverse
        ? !areOffersEmpty
            ? 'mt-3 scroll-mt-24'
            : 'scroll-mt-24'
        : 'scroll-mt-24';

    return (
        <>
            {showSellerCatalogNavigation && (
                <SellerCatalogNavigation
                    sectionTestId="seller-product-types-section"
                    universes={universeNavigationItems}
                />
            )}

            <section className={catalogSectionClass}>
                <SellerCatalogSearch
                    query={searchQuery}
                    loading={loading}
                    onSearch={handleCatalogSearch}
                    actions={
                        currentUser ? (
                            <SellerWishlistFilterButton
                                active={wishlistOnly}
                                loading={loading}
                                onClick={toggleWishlistFilter}
                            />
                        ) : undefined
                    }
                />

                <div
                    id="seller-catalog"
                    className={`container px-4 sm:px-6 lg:px-8 ${catalogContainerClass}`}
                >
                    <DesktopFiltersLayout
                        sidebar={
                            showSellerFilters ? (
                                <DesktopFilters
                                    filters={q}
                                    onFilterChange={applySearch}
                                    onResetFilters={resetFilters}
                                    expansion={currentExpansion}
                                    expansions={userExpansions}
                                    topContent={
                                        desktopSellerProductTypeNavigation
                                    }
                                    variant="seller"
                                />
                            ) : null
                        }
                    >
                        <SellerOffersGrid
                            areOffersEmpty={areOffersEmpty}
                            offers={offers ?? []}
                            offerProperties={offerProperties ?? []}
                            nextPage={nextPage}
                            loading={loading}
                            loadingMore={loadingMore}
                            loadMore={loadMore}
                            resetFilters={resetFilters}
                            sort={sort}
                            onSortChange={handleSortChange}
                            currentProductType={currentProductType}
                            heading={catalogHeading}
                            resultsCount={catalogCount}
                            subtitle={catalogSubtitle}
                            searchQuery={searchQuery}
                            clearSearch={() => handleCatalogSearch('')}
                            withDesktopSidebar={showSellerFilters}
                        />
                    </DesktopFiltersLayout>
                </div>
            </section>

            {showSellerFilters && (
                <MobileFilters
                    filters={q}
                    onFilterChange={applySearch}
                    expansion={currentExpansion}
                    topContent={mobileSellerProductTypeNavigation}
                    variant="seller"
                />
            )}
        </>
    );
};

export default Show;
